import { z } from 'zod';
import { MESSAGE_MAX_ATTACHMENTS, type Attachment } from './attachments.js';
import type { IsoDate } from './common.js';
import type { TaskStatus } from './tasks.js';
import type { UserSummary } from './users.js';
import type { MessageEmbed } from './webhooks.js';

export const MESSAGE_MAX_LENGTH = 4000;

export const messageBodySchema = z.string().trim().min(1, 'Mensaje vacío').max(MESSAGE_MAX_LENGTH);

export const sendMessageSchema = z
  .object({
    body: z.string().trim().max(MESSAGE_MAX_LENGTH).default(''),
    /** Files previously uploaded with `POST /channels/:id/attachments`. */
    attachmentIds: z.array(z.uuid()).max(MESSAGE_MAX_ATTACHMENTS).default([]),
    /** Client-generated id so the sender can reconcile its optimistic message. */
    clientId: z.string().max(64).optional(),
    /** Replies to a thread: the message that started it. */
    parentId: z.uuid().optional(),
    /** Inline reply: the message being quoted. */
    replyToId: z.uuid().optional(),
  })
  .refine((m) => m.body.length > 0 || m.attachmentIds.length > 0, {
    message: 'Mensaje vacío',
    path: ['body'],
  });
export type SendMessageInput = z.input<typeof sendMessageSchema>;

export const editMessageSchema = z.object({ body: messageBodySchema });
export type EditMessageInput = z.infer<typeof editMessageSchema>;

export const listMessagesQuerySchema = z.object({
  before: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListMessagesQuery = {
  before?: string;
  limit?: number;
};
/** Parsed query as seen by the server (defaults applied). */
export type ListMessagesParams = z.output<typeof listMessagesQuerySchema>;

export interface LinkedTask {
  id: string;
  title: string;
  status: TaskStatus;
}

/** Emoji reaction totals on a message. */
export interface MessageReaction {
  emoji: string;
  count: number;
  /** Who reacted (to highlight your own reaction and show names on hover). */
  userIds: string[];
}

/** Open Graph preview of a link in the message. */
export interface LinkPreview {
  url: string;
  title: string;
  description: string | null;
  imageUrl: string | null;
  siteName: string | null;
}

/** The message an inline reply quotes. */
export interface QuotedMessage {
  id: string;
  author: UserSummary;
  /** Start of the quoted text (mentions still encoded). */
  body: string;
  attachmentCount: number;
}

/** Summary of a message's thread. */
export interface ThreadSummary {
  replyCount: number;
  lastReplyAt: IsoDate | null;
  /** Up to 3 people who replied, most recent first. */
  participants: UserSummary[];
}

export const reactionEmojiSchema = z
  .string()
  .min(1)
  .max(32)
  .refine((v) => !/[\s<>]/.test(v), 'Emoji no válido');

/** A call that happened in a text conversation (shown as a card in the timeline). */
export interface MessageCall {
  startedAt: IsoDate;
  /** Null while the call is in progress. */
  endedAt: IsoDate | null;
  /** Everyone who joined at some point. */
  participantIds: string[];
}

export interface Message {
  id: string;
  channelId: string;
  teamId: string;
  /** For webhook messages this is the webhook's display identity. */
  author: UserSummary;
  /** Set when the message was posted by a channel webhook (integration/bot). */
  webhookId: string | null;
  body: string;
  createdAt: IsoDate;
  editedAt: IsoDate | null;
  /** Tasks created from this message. */
  tasks: LinkedTask[];
  attachments: Attachment[];
  embeds: MessageEmbed[];
  /** Present on call records. */
  call: MessageCall | null;
  /** Set on thread replies. */
  parentId: string | null;
  /** Inline reply: the quoted message (null if none or it was deleted). */
  replyTo: QuotedMessage | null;
  /** Set on messages that started a thread. */
  thread: ThreadSummary | null;
  reactions: MessageReaction[];
  previews: LinkPreview[];
  pinnedAt: IsoDate | null;
  pinnedBy: UserSummary | null;
  /** Set on the message that announces a scheduled meeting. */
  meetingId: string | null;
  /** Set on polls (the body holds the question too, for search and previews). */
  poll: MessagePoll | null;
  clientId?: string;
}

/* ---------- Polls ---------- */

export interface PollOption {
  id: string;
  text: string;
}

/** What is stored with the message. */
export interface PollDefinition {
  question: string;
  options: PollOption[];
  /** Several answers per person. */
  multiple: boolean;
  /** Voting stops at this moment (null: until someone closes it). */
  closesAt: IsoDate | null;
  /** Closed by hand. */
  closedAt: IsoDate | null;
}

export interface MessagePoll extends PollDefinition {
  options: (PollOption & { voterIds: string[] })[];
  /** Closed by hand or past `closesAt`. */
  closed: boolean;
}

export const createPollSchema = z.object({
  question: z.string().trim().min(1, 'Obligatorio').max(300),
  options: z
    .array(z.string().trim().min(1, 'Obligatorio').max(100))
    .min(2, 'Añade al menos 2 opciones')
    .max(10, 'Máximo 10 opciones'),
  multiple: z.boolean().default(false),
  /** Hours until it closes (null: open until closed by hand). */
  durationHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 30)
    .nullable()
    .default(null),
  parentId: z.uuid().optional(),
});
export type CreatePollInput = z.input<typeof createPollSchema>;

export const votePollSchema = z.object({ optionIds: z.array(z.string().min(1).max(20)).max(10) });
export type VotePollInput = z.infer<typeof votePollSchema>;

/**
 * Mentions are stored in message bodies as `<@userId>` (like Discord) so renames never break
 * them; `@canal` notifies everyone in the conversation.
 */
export const MENTION_PATTERN = /<@([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})>/g;
export const CHANNEL_MENTION_PATTERN = /(^|\s)@canal\b/;

export function mentionedUserIds(body: string): string[] {
  return [...new Set([...body.matchAll(MENTION_PATTERN)].map((m) => m[1] ?? ''))].filter(Boolean);
}

/** Role mentions are stored as `<@&roleId>`. */
export const ROLE_MENTION_PATTERN =
  /<@&([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})>/g;

export function mentionedRoleIds(body: string): string[] {
  return [...new Set([...body.matchAll(ROLE_MENTION_PATTERN)].map((m) => m[1] ?? ''))].filter(
    Boolean,
  );
}
